<template>
    <v-container fluid>
        <v-row>
            <v-col cols="12">
                <h1 class="text-left">Power-Up / Reboot History for
                    <b v-if="device">{{ device.slno }}</b>
                </h1>
            </v-col>
            <v-col cols="4">
                <v-select v-model="selectedDuration"
                          :items="durations"
                          :loading="processing"
                          label="Select Duration"
                          @update:model-value="fetchHistory"/>
            </v-col>
        </v-row>

        <v-row v-if="summary">
            <v-col cols="12" sm="6" md="4" lg="2" v-for="kpi in kpiCards" :key="kpi.label">
                <v-card variant="tonal" :color="kpi.color">
                    <v-card-item>
                        <v-card-title class="text-h5">{{ kpi.value }}</v-card-title>
                        <v-card-subtitle>{{ kpi.label }}</v-card-subtitle>
                        <small v-if="kpi.hint" class="text-medium-emphasis">{{ kpi.hint }}</small>
                    </v-card-item>
                </v-card>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12">
                <v-card variant="outlined">
                    <v-card-title>Reboots per Day</v-card-title>
                    <v-card-text>
                        <v-progress-circular v-if="processing" color="primary" indeterminate/>
                        <canvas ref="dailyChartEl" :style="{height: '320px'}"/>
                        <v-btn v-if="dailyChart" class="float-left mt-2" color="black" density="compact"
                               variant="outlined" @click="dailyChart.resetZoom()">Reset Zoom
                        </v-btn>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <v-row v-if="summary && summary.total === 0">
            <v-col cols="12">
                <v-alert type="info" variant="tonal" density="compact">
                    No reboot events for this device in the selected period.
                </v-alert>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12">
                <v-card variant="outlined">
                    <v-card-title>Hour-of-Day Pattern ({{ timezone }})</v-card-title>
                    <v-card-subtitle>Total reboots grouped by event-time hour in the user's timezone. Clusters at specific hours often point to a periodic trigger.</v-card-subtitle>
                    <v-card-text>
                        <canvas ref="hourlyChartEl" :style="{height: '260px'}"/>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script setup>
import {ref, computed, onMounted, onBeforeUnmount} from 'vue';
import {useRouter} from "vue-router";
import zoomPlugin from 'chartjs-plugin-zoom';
import {send} from "@/composables/apiClient";
import {Chart, registerables} from 'chart.js';
import {useSnackbarStore} from "@/stores/snackBarStore";

Chart.register(...registerables, zoomPlugin);

const router = useRouter();
const assetId = ref(router.currentRoute.value?.params?.asset ?? null);

const selectedDuration = ref('one_month');
const {showSnackbar} = useSnackbarStore();
const processing = ref(false);
const dailyChartEl = ref(null);
const hourlyChartEl = ref(null);
const device = ref(null);
const summary = ref(null);
const daily = ref([]);
const hourly = ref([]);
const timezone = ref('UTC');
let dailyChart;
let hourlyChart;

// Counter used to drop late responses when the user changes duration rapidly.
let requestSeq = 0;

const durations = [
    {value: 'one_week', title: '1 Week'},
    {value: 'two_week', title: '2 Week'},
    {value: 'one_month', title: '1 Month'},
    {value: 'two_month', title: '2 Month'},
    {value: 'three_month', title: '3 Month'},
    {value: 'six_month', title: '6 Months'},
    {value: 'one_year', title: '1 Year'},
    {value: 'two_year', title: '2 Year'},
    {value: 'five_year', title: '5 Year'},
    {value: 'all', title: 'All'},
];

const formatRelative = (iso) => {
    if (!iso) return '-';
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return iso;
    const diffSec = Math.max(0, Math.floor((Date.now() - then) / 1000));
    if (diffSec < 60) return `${diffSec}s ago`;
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
    if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
    return `${Math.floor(diffSec / 86400)}d ago`;
};

const formatGap = (sec) => {
    if (sec === null || sec === undefined) return '-';
    if (sec < 60) return `${sec}s`;
    if (sec < 3600) return `${Math.round(sec / 60)}m`;
    if (sec < 86400) return `${Math.round(sec / 3600)}h`;
    return `${Math.round(sec / 86400)}d`;
};

const kpiCards = computed(() => {
    const s = summary.value;
    if (!s) return [];
    const hasGap = s.min_gap_seconds !== null && s.min_gap_seconds !== undefined;
    const tightLoopWarn = hasGap && s.min_gap_seconds < 60;
    let gapColor = 'secondary';
    let gapHint  = '';
    if (hasGap) {
        gapColor = tightLoopWarn ? 'error' : 'success';
        gapHint  = tightLoopWarn ? 'tight-loop indicator' : 'between consecutive reboots';
    }
    return [
        {label: 'Total Reboots',  value: s.total,             color: 'primary'},
        {label: 'Mean per Day',   value: s.mean_per_day,      color: 'info'},
        {label: 'Peak Day',
            value: s.total > 0 ? s.peak_count : '-',
            color: s.peak_count > 50 ? 'error' : 'info',
            hint: s.peak_date ?? ''},
        {label: 'Last Reboot',
            value: formatRelative(s.last_reboot),
            color: 'secondary',
            hint: s.last_reboot ?? ''},
        {label: 'Min Gap',        value: formatGap(s.min_gap_seconds),  color: gapColor, hint: gapHint},
        {label: 'Period',         value: `${s.period_days}d`, color: 'secondary'},
    ];
});

// Daily bar chart options (anomaly colouring done in renderDaily).
const dailyOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {mode: 'index', intersect: false},
    plugins: {
        legend: {display: false},
        tooltip: {callbacks: {label: (ctx) => `${ctx.parsed.y} reboot${ctx.parsed.y === 1 ? '' : 's'}`}},
        title: {display: false},
        zoom: {
            pan: {mode: 'x', enabled: true},
            zoom: {wheel: {enabled: true}, pinch: {enabled: true}, mode: 'x'}
        }
    },
    scales: {
        x: {title: {display: true, text: 'Date'}, ticks: {maxRotation: 45, minRotation: 45}},
        y: {beginAtZero: true, ticks: {precision: 0}, title: {display: true, text: 'Reboots'}}
    }
};

const hourlyOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: {display: false},
        tooltip: {callbacks: {label: (ctx) => `${ctx.parsed.y} reboot${ctx.parsed.y === 1 ? '' : 's'}`}},
        title: {display: false}
    },
    scales: {
        x: {title: {display: true, text: 'Hour of Day (0-23)'}},
        y: {beginAtZero: true, ticks: {precision: 0}, title: {display: true, text: 'Reboots'}}
    }
};

const renderDaily = () => {
    if (dailyChart) dailyChart.destroy();
    const labels = daily.value.map(d => d.date);
    const counts = daily.value.map(d => d.count);
    const mean = summary.value?.mean_per_day ?? 0;
    const threshold = Math.max(50, mean * 3);
    const colors = counts.map(c => c > threshold ? '#e53935' : '#1e88e5');

    dailyChart = new Chart(dailyChartEl.value, {
        type: 'bar',
        data: {
            labels,
            datasets: [{
                data: counts,
                backgroundColor: colors,
                borderColor: colors,
                borderWidth: 1,
            }],
        },
        options: dailyOptions
    });
};

const renderHourly = () => {
    if (hourlyChart) hourlyChart.destroy();
    const labels = hourly.value.map(h => `${h.hour}:00`);
    const counts = hourly.value.map(h => h.count);
    hourlyChart = new Chart(hourlyChartEl.value, {
        type: 'bar',
        data: {
            labels,
            datasets: [{
                data: counts,
                backgroundColor: '#7e57c2',
                borderColor: '#7e57c2',
                borderWidth: 1,
            }],
        },
        options: hourlyOptions
    });
};

const fetchHistory = async () => {
    if (!assetId.value) return;
    processing.value = true;
    const seq = ++requestSeq;
    send('get', `devices/${assetId.value}/reboot-history`, {duration: selectedDuration.value})
        .then((response) => {
            if (seq !== requestSeq) return;   // stale response, a newer fetch is in flight
            const data = response.data.data;
            device.value   = data.device;
            summary.value  = data.summary;
            daily.value    = data.daily ?? [];
            hourly.value   = data.hourly ?? [];
            timezone.value = data.summary?.timezone ?? 'UTC';
            renderDaily();
            renderHourly();
        }).catch(() => {
            if (seq !== requestSeq) return;
            showSnackbar('Reboot history could not be loaded', 'error');
        }).finally(() => {
            if (seq === requestSeq) processing.value = false;
        });
};

onMounted(fetchHistory);

onBeforeUnmount(() => {
    if (dailyChart) {
        dailyChart.destroy();
        dailyChart = null;
    }
    if (hourlyChart) {
        hourlyChart.destroy();
        hourlyChart = null;
    }
});
</script>

<style scoped>
.main-container {
    height: calc(100vh - 100px) !important;
    overflow-y: hidden;
}
</style>
